import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import { API_ENDPOINTS } from '../../config/api';

const colors = getThemeColors('dark');

const UserDashboard: React.FC = () => {
  const [userStats, setUserStats] = useState({
    totalChamas: 0,
    totalMeetings: 0,
    totalContributions: 0,
    walletBalance: 0,
  });

  useEffect(() => {
    // Load user statistics on component mount
    loadUserStatistics();
  }, []);

  const loadUserStatistics = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No auth token found');
        return;
      }

      // Fetch user statistics from backend
      const response = await fetch(API_ENDPOINTS.USER_STATISTICS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setUserStats({
          totalChamas: data.data.chama_stats?.total_chamas || 0,
          totalMeetings: data.data.meeting_stats?.active_meetings || 0,
          totalContributions: data.data.contribution_stats?.total_contributions || 0,
          walletBalance: data.data.wallet_stats?.personal_balance || 0,
        });
      } else {
        console.error('Failed to load user statistics:', data.error);
        // Fallback to mock data if API fails
        setUserStats({
          totalChamas: 0,
          totalMeetings: 0,
          totalContributions: 0,
          walletBalance: 0,
        });
      }
    } catch (error) {
      console.error('Error loading user statistics:', error);
      // Fallback to mock data if network fails
      setUserStats({
        totalChamas: 0,
        totalMeetings: 0,
        totalContributions: 0,
        walletBalance: 0,
      });
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserFirstName = (user: any) => {
    return user?.first_name || user?.firstName || 'User';
  };

  const getCurrentUser = () => {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  };

  const renderHeader = () => (
    <div style={{ ...styles.header, backgroundColor: colors.background }}>
      <div style={styles.headerGradient}>
        <div style={styles.headerContent}>
          <div style={styles.userInfo}>
            <div style={styles.greetingContainer}>
              <div style={{ ...styles.greeting, color: colors.textSecondary }}>
                {getGreeting().toUpperCase()}
              </div>
              <div style={{ ...styles.userName, color: colors.text }}>
                Welcome back, {getUserFirstName(getCurrentUser())}
              </div>
              <div style={{ ...styles.userSubtitle, color: colors.textSecondary }}>
                Here's what's happening with your finances today
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const quickActions = [
    {
      id: 1,
      title: 'AI Assistant',
      icon: '🤖',
      color: colors.secondary,
      onPress: () => window.location.href = '/dashboard/ai-assistant',
    },
    {
      id: 2,
      title: 'Wallet',
      icon: '💳',
      color: colors.primary,
      onPress: () => window.location.href = '/dashboard/wallet',
    },
    {
      id: 3,
      title: 'Groups',
      icon: '👥',
      color: colors.secondary,
      onPress: () => window.location.href = '/dashboard/mychamas',
    },
    {
      id: 4,
      title: 'Reminders',
      icon: '🔔',
      color: colors.warning,
      onPress: () => window.location.href = '/dashboard/reminders',
    },
    {
      id: 5,
      title: 'Meetings',
      icon: '📅',
      color: colors.primary,
      onPress: () => window.location.href = '/dashboard/meetings',
    },
    {
      id: 6,
      title: 'Chat',
      icon: '💬',
      color: colors.secondary,
      onPress: () => window.location.href = '/dashboard/chat',
    },
    {
      id: 7,
      title: 'Transactions',
      icon: '📄',
      color: colors.warning,
      onPress: () => window.location.href = '/dashboard/transactions',
    },
    {
      id: 8,
      title: 'Profile',
      icon: '👤',
      color: colors.info,
      onPress: () => window.location.href = '/dashboard/profile',
    },
  ];


  const renderQuickStats = () => (
    <div style={styles.statsCard}>
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statTile, backgroundColor: colors.surface, borderColor: colors.border }}>
          <div style={styles.statTileHeader}>
            <div style={{ ...styles.statTileIcon, backgroundColor: colors.primary + '15' }}>
              <span style={{ color: colors.primary }}>💰</span>
            </div>
            <span style={{ ...styles.statTileLabel, color: colors.textSecondary }}>Wallet Balance</span>
          </div>
          <span style={{ ...styles.statTileValue, color: colors.text }}>
            Ksh {userStats.walletBalance.toLocaleString()}
          </span>
        </div>

        <div style={{ ...styles.statTile, backgroundColor: colors.surface, borderColor: colors.border }}>
          <div style={styles.statTileHeader}>
            <div style={{ ...styles.statTileIcon, backgroundColor: colors.secondary + '15' }}>
              <span style={{ color: colors.secondary }}>👥</span>
            </div>
            <span style={{ ...styles.statTileLabel, color: colors.textSecondary }}>Total Chamas</span>
          </div>
          <span style={{ ...styles.statTileValue, color: colors.text }}>
            {userStats.totalChamas}
          </span>
        </div>

        <div style={{ ...styles.statTile, backgroundColor: colors.surface, borderColor: colors.border }}>
          <div style={styles.statTileHeader}>
            <div style={{ ...styles.statTileIcon, backgroundColor: colors.warning + '15' }}>
              <span style={{ color: colors.warning }}>📅</span>
            </div>
            <span style={{ ...styles.statTileLabel, color: colors.textSecondary }}>Active Meetings</span>
          </div>
          <span style={{ ...styles.statTileValue, color: colors.text }}>
            {userStats.totalMeetings}
          </span>
        </div>

        <div style={{ ...styles.statTile, backgroundColor: colors.surface, borderColor: colors.border }}>
          <div style={styles.statTileHeader}>
            <div style={{ ...styles.statTileIcon, backgroundColor: colors.success + '15' }}>
              <span style={{ color: colors.success }}>📈</span>
            </div>
            <span style={{ ...styles.statTileLabel, color: colors.textSecondary }}>Contributions</span>
          </div>
          <span style={{ ...styles.statTileValue, color: colors.text }}>
            {userStats.totalContributions}
          </span>
        </div>
      </div>
    </div>
  );

  const renderQuickActions = () => (
    <div style={{ ...styles.section, backgroundColor: colors.surface, borderRadius: borderRadius.lg, padding: spacing.lg, border: `1px solid ${colors.border}`, ...getShadowStyle('sm') }}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Quick Actions</h2>
      <div style={styles.quickActionsGrid}>
        {quickActions.map((action) => (
          <div
            key={action.id}
            style={styles.quickActionItem}
            onClick={action.onPress}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: action.color }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                {action.icon}
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              {action.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );


  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Dashboard"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        {renderHeader()}
        {renderQuickStats()}
        {renderQuickActions()}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
    paddingBottom: 80, // Space for tab bar
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80, // Space for fixed header
    paddingBottom: 80, // Space for fixed tab bar
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  header: {
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    paddingLeft: 0,
    paddingRight: 0,
  },
  headerGradient: {
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    backgroundColor: colors.primary + '05',
    borderBottomWidth: 1,
    borderBottomColor: colors.primary + '10',
    borderBottomStyle: 'solid',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  userInfo: {
    flex: 1,
  },
  greetingContainer: {
    marginBottom: spacing.sm,
  },
  greeting: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    marginBottom: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: colors.textSecondary,
  },
  userName: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: spacing.xs,
    lineHeight: '40px',
    color: colors.text,
    margin: 0,
  },
  userSubtitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.lineHeight.normal,
    color: colors.textSecondary,
    margin: 0,
  },
  statsCard: {
    margin: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    ...getShadowStyle('md'),
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: spacing.md,
  },
  statTile: {
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    borderWidth: 1,
    borderStyle: 'solid',
  },
  statTileHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  statTileIcon: {
    width: 32,
    height: 32,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  statTileLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    flex: 1,
  },
  statTileValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    textAlign: 'left',
  },
  section: {
    marginLeft: spacing.md,
    marginRight: spacing.md,
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing.lg,
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
    gap: spacing.sm,
    justifyItems: 'center',
  },
  quickActionItem: {
    alignItems: 'center',
    marginBottom: spacing.md,
    padding: spacing.sm,
    cursor: 'pointer',
  },
  quickActionIcon: {
    width: 48,
    height: 48,
    borderRadius: borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  quickActionText: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    fontWeight: typography.fontWeight.medium,
  },
  walletCard: {
    marginLeft: spacing.md,
    marginRight: spacing.md,
    marginBottom: spacing.md,
  },
  walletCardContent: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    borderWidth: 1,
    borderStyle: 'solid',
    ...getShadowStyle('md'),
  },
  walletCardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  walletCardIcon: {
    width: 48,
    height: 48,
    borderRadius: borderRadius.lg,
    backgroundColor: colors.primary + '15',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  walletCardInfo: {
    flex: 1,
  },
  walletCardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.xs,
  },
  walletCardBalance: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
  },
  walletCardActions: {
    display: 'flex',
    gap: spacing.sm,
  },
  walletActionButton: {
    flex: 1,
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    border: 'none',
    color: colors.text,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};

export default UserDashboard;
